/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '../../app/redux/hooks';
import { appSubscriptionService, CardFailureLockEvent } from '../../common/subscriptions/appSubscriptionService';
import { useLazyOperatorSessionGetByFobQuery } from '../api';
import { useWorkstation } from '../appState';
import { showErrorDialog } from '../error-dialog/errorDialogSlice';

export default function CardFailureLock() {
  const dispatch = useAppDispatch();
  const workstation = useWorkstation();
  const [state, setState] = useState<CardFailureLockEvent | undefined>(undefined);
  const [fob, setFob] = useState<string | undefined>(undefined);
  const [fobFail, setFobFail] = useState(false);
  const keyboardState = useRef<string>('');

  const [triggerFobCheck] = useLazyOperatorSessionGetByFobQuery();

  const showFailureDialog = useCallback(
    (s: CardFailureLockEvent) => {
      const priceFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
      dispatch(
        showErrorDialog({
          errorType: 'card-payment-failure',
          message: s.message,
          dismissible: true,
          buttonText: 'Payment Failed',
          backgroundColor: 'red',
          alternateButtonText: 'Payment Completed',
          showAlternateButton: true,
          requireConfirmationOnClose: true,
          confirmNoText: 'Back',
          confirmYesText: 'Confirm Failed',
          confirmAlternateNoText: 'Back',
          confirmAlternateYesText: 'Enter Auth Code',
          confirmationText: `Are you sure the payment failed?<br /><br /> Please confirm you checked the ${
            s.pedName
          } card machine and have proof the payment declined for ${priceFormatter.format(s.amount)}`,
          confirmationTextAlternate: `Please confirm the payment succeeded,<br /><br /> You will need to enter the Auth Code off the card machine.<br /><br /><b>Amount:</b> ${priceFormatter.format(
            s.amount,
          )}<br /><b>Card Machine:</b> ${s.pedName}`,
          state: {
            transactionId: s.transactionId,
            transactionNumber: s.transactionNumber,
            sequenceNumber: s.sequenceNumber,
            operatorName: s.operatorName,
            operatorId: s.operatorId,
            amount: s.amount,
            reason: s.reason,
            cardRequestId: s.cardRequestId,
            message: s.message,
            pedName: s.pedName,
            pedTerminalId: s.pedTerminalId,
            type: 'card-failure',
          },
          alternateState: {
            transactionId: s.transactionId,
            transactionNumber: s.transactionNumber,
            sequenceNumber: s.sequenceNumber,
            operatorName: s.operatorName,
            operatorId: s.operatorId,
            amount: s.amount,
            reason: s.reason,
            cardRequestId: s.cardRequestId,
            message: s.message,
            pedName: s.pedName,
            pedTerminalId: s.pedTerminalId,
            type: 'offline-card',
          },
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    const sub = appSubscriptionService.cardFailureLockObservable().subscribe((val) => {
      if (workstation?.hasFobReader === false) {
        showFailureDialog(val);
        return;
      }
      setState(val);
      setFob(undefined);
      setFobFail(false);
    });
    return () => sub.unsubscribe();
  }, [workstation?.hasFobReader, showFailureDialog]);

  useEffect(() => {
    if (!state) return;
    const eventHandler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const regexSignOn = /^0100([a-z|A-Z|0-9]{12})$/gm;
        const regexSignOnMatch = keyboardState.current.match(regexSignOn);
        if (regexSignOnMatch !== null) setFob(regexSignOnMatch[0]);
        keyboardState.current = '';
      } else {
        if (e.key.length === 1) {
          if (keyboardState.current.length >= 16) keyboardState.current = keyboardState.current.substring(1) + e.key;
          else keyboardState.current = keyboardState.current + e.key;
        }
      }
    };
    window.addEventListener('keydown', eventHandler);
    return () => window.removeEventListener('keydown', eventHandler);
  }, [state]);

  useEffect(() => {
    if (!fob || !state) return;
    triggerFobCheck(fob)
      .unwrap()
      .then((response) => {
        if (response.operator.id === state.operatorId) {
          showFailureDialog(state);
          setState(undefined);
          setFob(undefined);
          setFobFail(false);
        } else {
          setFob(undefined);
          setFobFail(true);
        }
      })
      .catch(() => {
        setFob(undefined);
        setFobFail(true);
      });
  }, [fob, showFailureDialog, state, triggerFobCheck]);

  if (!state) return <></>;

  const priceFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

  return (
    <div className="pos-modal-portal">
      <div className="pos-modal-overlay pos-modal-background-red">
        <div className="pos-popup pos-popup_role_error">
          <div className="pos-popup__content">
            <div className="pos-popup__icon"></div>
            <div className="pos-popup__info">
              <h3 className="pos-popup__title">Card Payment Failed</h3>
              <p className="pos-popup__text">
                <strong>Transaction:</strong> {state.transactionNumber}/{state.sequenceNumber}
                <br />
                <strong>Card Machine:</strong> {state.pedName}
                <br />
                <strong>Amount:</strong> {priceFormatter.format(state.amount)}
                <br />
                <br />
                Till locked — <strong>{state.operatorName}</strong> must scan their fob to continue.
                <br />
                {fob !== undefined && <em>Validating fob, please wait...</em>}
                {fobFail && <span style={{ color: 'red' }}>Invalid fob — please scan {state.operatorName}&apos;s fob.</span>}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
